import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavBar, Toast, Button, Checkbox } from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import { t } from 'i18next';
import DynamSoftScanComponent from 'components/ScanComponent/DynamSoftScanComponent';
import customTopToast from 'components/CustomTopToast';
import {ExclamationCircleFill, ScanningOutline} from "antd-mobile-icons";
import { playScanSound } from "../../../utils/scanUtils";
import {ExceptionDictionaryType, ExceptionSubReasonType} from "../graphql/queries/getExceptionDictionaryForDelivery";
import { openTrackNumCamera } from "../../../utils/appRuntime";
import { useTempScanPhotos } from '../store/deliveryStore';

export interface ParcelItem {
    trackingNumber: string;
    stopNumber: string;
    deliveryTaskOrderId?: number;
}

export interface ScannedItem {
    trackingNumber: string;
    stopNumber: string;
    frameDataUrl?: string;
    deliveryTaskOrderId?: number;
}

const ScanVerificationTemporaryPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const targetPath =  location.state?.targetPath;
    const exceptionReason: ExceptionDictionaryType = location.state?.exceptionReason;
    const exceptionSubReasons: ExceptionSubReasonType[] = location.state?.exceptionSubReasons ?? (location.state?.exceptionSubReason ? [location.state.exceptionSubReason] : []);
    const parcels: ParcelItem[] = location.state?.parcels;
    const { setTempScanPhotos } = useTempScanPhotos();

    const expectedSet = useMemo(() => new Set(parcels.map(p => p.trackingNumber)), [parcels]);
    const orderTotal = parcels.length;

    const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
    const [activeTab, setActiveTab] = useState<'scanned' | 'unscanned'>('scanned');
    const lastScanTimeRef = useRef<Map<string, number>>(new Map());
    
    // 网络状态检测
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

    // 无网络模式下的选中包裹列表
    const [selectedTrackingNumbers, setSelectedTrackingNumbers] = useState<Set<string>>(new Set());

    // 标记是否正在等待拍照完成
    const [isWaitingForPhoto, setIsWaitingForPhoto] = useState<boolean>(false);

    // 标记相机是否已打开（用于处理取消拍照无回调的场景）
    const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);

    // 控制扫描组件暂停状态
    const [isScanPaused, setIsScanPaused] = useState<boolean>(false);

    // 强制重建扫描组件（仅本页面使用，不影响其他页面复用）
    const [scanComponentKey, setScanComponentKey] = useState<number>(0);

    // 控制扫描组件是否销毁（进入拍照页前销毁释放资源）
    const [isScanComponentDestroyed, setIsScanComponentDestroyed] = useState<boolean>(false);
    
    // 标记是否需要用户手势来激活摄像头（初次进入或从拍照页返回时）
    const [needsUserGesture, setNeedsUserGesture] = useState<boolean>(
        () => location.state?.needsUserGesture !== undefined ? location.state.needsUserGesture : false
    );

    useEffect(() => {
        sessionStorage.setItem('returningFromDetail', 'true');
        
        // 页面卸载时清理资源
        return () => {
            // 确保扫描组件被标记为销毁状态
            setIsScanComponentDestroyed(true);
        };
    }, []);

    // 监听网络状态变化
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // 从相机返回后的恢复：部分 WebView 可能不触发 openCamera 回调/visibilitychange，故监听多事件兜底
    const cameraResumeStateRef = useRef<{ isCameraOpen: boolean; isScanPaused: boolean }>({
        isCameraOpen: false,
        isScanPaused: false
    });
    useEffect(() => {
        cameraResumeStateRef.current = { isCameraOpen, isScanPaused };
    }, [isCameraOpen, isScanPaused]);

    const resumeFromCameraRef = useRef<() => void>(() => {});
    resumeFromCameraRef.current = () => {
        const shouldResume = cameraResumeStateRef.current.isCameraOpen || cameraResumeStateRef.current.isScanPaused;
        if (!shouldResume) return;

        setTimeout(() => {
            setIsCameraOpen(false);
            setIsWaitingForPhoto(false);
            setIsScanPaused(false);
            
            // 延迟重新初始化扫描组件，确保资源完全释放
            setTimeout(() => {
                setIsScanComponentDestroyed(false);
                setNeedsUserGesture(true);
                setScanComponentKey((k) => k + 1);
            }, 300);
        }, 200);
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) resumeFromCameraRef.current();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handleScanSuccess = async (id: string, frameDataUrl?: string) => {
        // 3秒内重复扫描校验
        const now = Date.now();
        const lastScanTime = lastScanTimeRef.current.get(id);
        if (lastScanTime && now - lastScanTime < 3000) {
            return;
        }

        if (!expectedSet.has(id)) {
            customTopToast.show({
                content: (
                    <div className="flex items-center justify-center gap-2">
                        <ExclamationCircleFill className="text-2xl text-yellow-500" />
                        <span className="text-xs text-white">{t('pages.delivery.task.transferIn.packageNotBelongToStop')}</span>
                    </div>
                ),
                position: 'top',
                duration: 2000
            });
            return;
        }
        const p = parcels.find(x => x.trackingNumber === id);
        if (!p) return;

        // 校验已扫描包裹数量是否超过50
        if (scannedItems.length >= 50) {
            customTopToast.show({
                content: (
                    <div className="flex items-center justify-center gap-2">
                        <ExclamationCircleFill className="text-2xl text-yellow-500" />
                        <span className="text-xs text-white">{t('pages.delivery.temporary.batchScan.maxBatchDeliveryLimit')}</span>
                    </div>
                ),
                position: 'top',
                duration: 2000
            });
            return;
        }

        // 更新最后扫描时间
        lastScanTimeRef.current.set(id, now);

        // 判断是否为手动输入（没有 frameDataUrl）
        const isManualInput = !frameDataUrl;

        await playScanSound();

        // 添加到扫描列表
        setScannedItems(prev => {
            if (prev.some(s => s.trackingNumber === id)) return prev;
            return [...prev, {
                trackingNumber: p.trackingNumber,
                stopNumber: p.stopNumber,
                frameDataUrl: isManualInput ? undefined : frameDataUrl, // 手动输入时初始无照片
                deliveryTaskOrderId: p.deliveryTaskOrderId
            }];
        });

        // 有网络且手动输入时，唤起拍照功能（不强制拍照）
        if (isOnline && isManualInput) {
            // 检查当前包裹是否已有图片，如果有则跳过拍照
            const currentItem = scannedItems.find(item => item.trackingNumber === id);
            if (currentItem) {
                return;
            }
            
            const photoTips = t('pages.delivery.temporary.common.takeLabelPhoto') || '';
            
            // 准备运单号数据
            const trackNumBeansJson = JSON.stringify([{ trackNum: id }]);
            
            // 判断是否是最后一个包裹
            const isLastParcel = scannedItems.length + 1 === parcels.length;
            
            // 如果是最后一个包裹，设置等待拍照状态
            if (isLastParcel) {
                setIsWaitingForPhoto(true);
            }

            // 打开相机前，先销毁扫描组件释放资源
            setIsScanComponentDestroyed(true);
            setIsCameraOpen(true);
            setIsScanPaused(true);

            // 等待足够时间确保组件已完全销毁并释放资源（至少300ms）
            await new Promise(resolve => setTimeout(resolve, 300));

            openTrackNumCamera(1, 1, photoTips, '', trackNumBeansJson, async (items) => {
                try {
                    if (items.length > 0) {
                        // 新的返回结构: { trackNum: string; photoBase64: string }[]
                        const photoItem = items.find(item => item.trackNum === id);
                        if (photoItem && photoItem.photoBase64) {
                            setScannedItems(prev => {
                                return prev.map(item =>
                                    item.trackingNumber === id
                                        ? { ...item, frameDataUrl: photoItem.photoBase64 }
                                        : item
                                );
                            });
                        }
                    }
                } finally {
                    // 无论拍照还是取消，都清除等待状态并重新初始化扫描组件
                    if (isLastParcel) {
                        setIsWaitingForPhoto(false);
                    }
                    setIsCameraOpen(false);
                    setIsScanPaused(false);
                    
                    // 延迟重新初始化，确保拍照页资源已完全释放
                    setTimeout(() => {
                        setIsScanComponentDestroyed(false);
                        setNeedsUserGesture(true);
                        setScanComponentKey((k) => k + 1);
                    }, 300);
                }
            });

        }
    };
    const handleScanSuccessRef = useRef(handleScanSuccess);
    handleScanSuccessRef.current = handleScanSuccess;
    const onScan = (id: string, frameDataUrl?: string) => { handleScanSuccessRef.current(id, frameDataUrl); };

    // 处理多选包裹的勾选/取消
    const handleToggleParcel = (trackingNumber: string) => {
        setSelectedTrackingNumbers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(trackingNumber)) {
                newSet.delete(trackingNumber);
            } else {
                // 校验是否已达到50单上限
                if (newSet.size >= 50) {
                    customTopToast.show({
                        content: (
                            <div className="flex items-center justify-center gap-2">
                                <ExclamationCircleFill className="text-2xl text-yellow-500" />
                                <span className="text-xs text-white">{t('pages.delivery.temporary.batchScan.maxBatchDeliveryLimit')}</span>
                            </div>
                        ),
                        position: 'top',
                        duration: 2000
                    });
                    return newSet;
                }
                newSet.add(trackingNumber);
            }
            return newSet;
        });
    };

    // 全选/取消全选
    const handleToggleAll = () => {
        if (selectedTrackingNumbers.size === parcels.length) {
            setSelectedTrackingNumbers(new Set());
        } else {
            // 全选时最多只选择前50单
            const trackingNumbers = parcels.map(p => p.trackingNumber);
            const limitedSelection = trackingNumbers.slice(0, 50);
            
            // 如果包裹总数超过50，显示提示
            if (trackingNumbers.length > 50) {
                customTopToast.show({
                    content: (
                        <div className="flex items-center justify-center gap-2">
                            <ExclamationCircleFill className="text-2xl text-yellow-500" />
                            <span className="text-xs text-white">{t('pages.delivery.temporary.batchScan.maxBatchDeliveryLimit')}</span>
                        </div>
                    ),
                    position: 'top',
                    duration: 2000
                });
            }
            
            setSelectedTrackingNumbers(new Set(limitedSelection));
        }
    };

    const handleNext = () => {
        // 判断当前使用哪种模式的数据
        const useOfflineMode = !isOnline || selectedTrackingNumbers.size > 0;
        
        if (useOfflineMode) {
            // 无网络模式或有选中包裹：使用选中的包裹数据
            if (selectedTrackingNumbers.size === 0) {
                Toast.show({
                    content: t('pages.delivery.temporary.batchScan.pleaseSelectParcels'),
                    position: 'center'
                });
                return;
            }

            // 根据选中的追踪号构建扫描结果
            const selectedItems = parcels
                .filter(p => selectedTrackingNumbers.has(p.trackingNumber))
                .map(p => ({
                    trackingNumber: p.trackingNumber,
                    stopNumber: p.stopNumber,
                    deliveryTaskOrderId: p.deliveryTaskOrderId
                }));

            const deliveryTaskOrderIdList = selectedItems
                .map(item => item.deliveryTaskOrderId)
                .filter((id): id is number => id !== undefined);

            // 导航到批量交付页面
            navigate(targetPath, {
                state: {
                    scanResults: selectedItems,
                    deliveryTaskOrderIdList: deliveryTaskOrderIdList,
                    exceptionReason: exceptionReason,
                    exceptionSubReasons: exceptionSubReasons,
                    activeTab: location.state?.activeTab,
                    fromMapMode: location.state?.fromMapMode,
                    fromScanVerification: true, // 标记从扫码验证页面跳转
                    ...(targetPath === "/delivery/temporary/mark-exception" ? {
                        deliveryTaskOrderId: selectedItems[0]?.deliveryTaskOrderId,
                        trackingNumber: selectedItems[0]?.trackingNumber
                    } : {})
                }
            });
        } else {
            // 在线扫描模式：使用扫描数据
            if (scannedItems.length === 0) {
                Toast.show({
                    content: t('pages.delivery.temporary.batchScan.pleaseScanParcels'),
                    position: 'center'
                });
                return;
            }

            // ✅ 新方案：将图片存储到 deliveryStore
            const photosMap = new Map<string, string>();
            scannedItems.forEach(item => {
                if (item.frameDataUrl && item.trackingNumber) {
                    photosMap.set(item.trackingNumber, item.frameDataUrl);
                }
            });
            setTempScanPhotos(photosMap);

            // ✅ 只传递追踪号列表（不传递 Base64）
            const lightweightScanResults = scannedItems.map(item => ({
                trackingNumber: item.trackingNumber,
                stopNumber: item.stopNumber,
                deliveryTaskOrderId: item.deliveryTaskOrderId
            }));

            const deliveryTaskOrderIdList = scannedItems
                .map(item => item.deliveryTaskOrderId)
                .filter((id): id is number => id !== undefined);

            // 导航到批量交付页面，携带扫描数据和 deliveryTaskOrderIdList
            navigate(targetPath, {
                state: {
                    scanResults: lightweightScanResults,
                    deliveryTaskOrderIdList: deliveryTaskOrderIdList,
                    exceptionReason: exceptionReason,
                    exceptionSubReasons: exceptionSubReasons,
                    activeTab: location.state?.activeTab,
                    fromMapMode: location.state?.fromMapMode,
                    fromScanVerification: true, // 标记从扫码验证页面跳转
                    ...(targetPath === "/delivery/temporary/mark-exception" ? {
                        deliveryTaskOrderId: lightweightScanResults[0]?.deliveryTaskOrderId,
                        trackingNumber: lightweightScanResults[0]?.trackingNumber
                    } : {})
                }
            });
        }
    };

    // 当所有包裹扫描完成后，自动跳转到下一个页面
    const hasAutoNavigatedRef = useRef(false);
    useEffect(() => {
        if (
            parcels.length > 0 &&
            scannedItems.length === parcels.length &&
            scannedItems.length > 0 &&
            !hasAutoNavigatedRef.current &&
            !isWaitingForPhoto // 确保没有正在等待拍照
        ) {
            hasAutoNavigatedRef.current = true;

            // ✅ 存储图片到 Store
            const photosMap = new Map<string, string>();
            scannedItems.forEach(item => {
                if (item.frameDataUrl && item.trackingNumber) {
                    photosMap.set(item.trackingNumber, item.frameDataUrl);
                }
            });
            setTempScanPhotos(photosMap);

            const timer = setTimeout(() => {
                // ✅ 轻量级数据传递
                const lightweightScanResults = scannedItems.map(item => ({
                    trackingNumber: item.trackingNumber,
                    stopNumber: item.stopNumber,
                    deliveryTaskOrderId: item.deliveryTaskOrderId
                }));

                const deliveryTaskOrderIdList = scannedItems
                    .map(item => item.deliveryTaskOrderId)
                    .filter((id): id is number => id !== undefined);

                // 导航到批量交付页面，携带扫描数据和 deliveryTaskOrderIdList
                navigate(targetPath, {
                    state: {
                        scanResults: lightweightScanResults,
                        deliveryTaskOrderIdList: deliveryTaskOrderIdList,
                        exceptionReason: exceptionReason,
                        exceptionSubReasons: exceptionSubReasons,
                        activeTab: location.state?.activeTab,
                        fromScanVerification: true, // 标记从扫码验证页面跳转
                        fromMapMode: location.state?.fromMapMode,
                        ...(targetPath === "/delivery/temporary/mark-exception" ? {
                            deliveryTaskOrderId: lightweightScanResults[0]?.deliveryTaskOrderId,
                            trackingNumber: lightweightScanResults[0]?.trackingNumber
                        } : {})
                    }
                });
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [scannedItems, parcels.length, navigate, isWaitingForPhoto]);

    // 已扫描列表倒序显示（最新扫描的在最上面）
    const scannedList = [...scannedItems].reverse();
    const unscannedList = parcels.filter(p => !scannedItems.some(s => s.trackingNumber === p.trackingNumber));
    const isAllSelected = selectedTrackingNumbers.size === parcels.length && parcels.length > 0;

    // 处理用户点击激活摄像头
    const handleActivateCamera = () => {
        setNeedsUserGesture(false);
        // 重新初始化扫描组件
        setScanComponentKey((k) => k + 1);
    };

    // 渲染有网络时的扫描模式
    const renderOnlineMode = () => {
        // 只有一条数据时，扫描组件铺满全屏
        if (parcels.length === 1) {
            return (
                <div className="absolute inset-0">
                    <DynamSoftScanComponent key={scanComponentKey} onScanSuccess={onScan} showManualInputIcon={true} isPaused={isScanPaused} />
                </div>
            );
        }

        // 多条数据时，显示扫描组件和列表
        return (
            <>
                <div 
                    className="absolute inset-0"
                    style={{ height: '48%', maxHeight: '48%' }}
                >
                    {/* 只在用户点击后才渲染扫描组件 */}
                    {!needsUserGesture && !isScanComponentDestroyed && (
                        <DynamSoftScanComponent key={scanComponentKey} onScanSuccess={onScan} showManualInputIcon={true} isPaused={isScanPaused} />
                    )}
                    
                    {/* 用户手势提示层 - 多包裹模式 */}
                    {needsUserGesture && (
                        <div 
                            className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/70 to-black/50 z-50"
                            onClick={handleActivateCamera}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="bg-white/95 backdrop-blur-sm rounded-3xl px-12 py-8 shadow-2xl transform transition-all hover:scale-105 active:scale-95">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="w-20 h-20 rounded-full flex items-center justify-center">
                                        <ScanningOutline width={68} height={68} color={`text-gray-500`}/>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span>{t('pages.delivery.temporary.batchScan.tapToStartScanning')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className="absolute bottom-0 left-0 right-0 flex flex-col bg-white rounded-t-3xl shadow-[0_-2px_12px_rgba(0,0,0,0.08)]"
                    style={{ height: '52%', maxHeight: '52%' }}
                >
                    <div className="px-4 pt-3 pb-1 text-base font-medium text-center">
                        {t('pages.delivery.temporary.batchScan.totalParcels', { count: orderTotal })}
                    </div>
                    <div className="flex border-b border-gray-200">
                        <button
                            type="button"
                            className={`flex-1 py-2 text-center font-bold text-sm ${activeTab === 'scanned' ? 'text-purple-600 font-semibold border-b-2 border-purple-600' : 'text-gray-400'}`}
                            onClick={() => setActiveTab('scanned')}
                        >
                            {t('pages.delivery.temporary.batchScan.scanned')}({scannedList.length})
                        </button>
                        <button
                            type="button"
                            className={`flex-1 py-2 text-center text-sm ${activeTab === 'unscanned' ? 'text-purple-600 font-semibold border-b-2 border-purple-600' : 'text-gray-400'}`}
                            onClick={() => setActiveTab('unscanned')}
                        >
                            {t('pages.delivery.temporary.batchScan.unScanned')}({unscannedList.length})
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {activeTab === 'scanned'
                            ? scannedList.map((item) => (
                                <div key={item.trackingNumber} className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                                    <div className="flex-1 text-base break-all text-gray-500">{item.trackingNumber}</div>
                                    <div className="w-16 text-center break-words font-bold">{item.stopNumber}</div>
                                </div>
                            ))
                            : unscannedList.map((item) => (
                                <div key={item.trackingNumber} className="flex items-center px-4 py-2 border-b border-gray-100">
                                    <div className="flex-1 text-base break-all text-gray-500">{item.trackingNumber}</div>
                                    <div className="w-16 text-center break-words font-bold">{item.stopNumber}</div>
                                </div>
                            ))}
                    </div>
                    <div {...getBottomNavBarContainerStyle('p-4 bg-white')}>
                        <Button
                            color="primary"
                            className="w-full rounded-lg text-lg font-bold"
                            onClick={handleNext}
                            disabled={scannedItems.length === 0}
                        >
                            {t('pages.delivery.temporary.batchScan.next')}
                        </Button>
                    </div>
                </div>
            </>
        );
    };

    // 渲染无网络时的多选模式
    const renderOfflineMode = () => {
        // 多条数据时，显示列表选择界面
        return (
            <div className="absolute inset-0 flex flex-col bg-white">
                {/* 网络状态提示 */}
                <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-100">
                    <div className="flex items-center gap-2">
                        <ExclamationCircleFill className="text-xl text-yellow-500" />
                        <span className="text-sm text-gray-700">
                            {t('pages.delivery.temporary.batchScan.offlineMode')}
                        </span>
                    </div>
                </div>

                {/* 顶部统计信息 */}
                <div className="px-4 pt-3 pb-2">
                    <div className="text-base font-medium text-center">
                        {t('pages.delivery.temporary.batchScan.totalParcels', { count: orderTotal })}
                    </div>
                    <div className="text-sm text-gray-500 text-center mt-1">
                        {t('pages.delivery.temporary.batchScan.selected')}: {selectedTrackingNumbers.size}
                    </div>
                </div>

                {/* 全选操作栏 */}
                <div className="px-4 py-2 border-y border-gray-200 bg-gray-50">
                    <label className="flex items-center cursor-pointer">
                        <Checkbox
                            checked={isAllSelected}
                            onChange={handleToggleAll}
                            className="mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">
                            {isAllSelected ? t('pages.delivery.temporary.batchScan.deselectAll') : t('pages.delivery.temporary.batchScan.selectAll')}
                        </span>
                    </label>
                </div>

                {/* 包裹列表 */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {parcels.map((parcel) => {
                        const isChecked = selectedTrackingNumbers.has(parcel.trackingNumber);
                        return (
                            <div
                                key={parcel.trackingNumber}
                                className={`flex items-center px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
                                    isChecked ? 'bg-purple-50' : 'bg-white hover:bg-gray-50'
                                }`}
                                onClick={() => handleToggleParcel(parcel.trackingNumber)}
                            >
                                <Checkbox
                                    checked={isChecked}
                                    onChange={() => handleToggleParcel(parcel.trackingNumber)}
                                    className="mr-3"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-base break-all text-gray-700 font-medium">
                                        {parcel.trackingNumber}
                                    </div>
                                </div>
                                <div className="w-16 text-center ml-2">
                                    <span className="inline-block px-2 py-1 text-sm font-bold rounded">
                                        {parcel.stopNumber}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 底部按钮 */}
                <div {...getBottomNavBarContainerStyle('p-4 bg-white border-t border-gray-200')}>
                    <Button
                        color="primary"
                        className="w-full rounded-lg text-lg font-bold"
                        onClick={handleNext}
                        disabled={selectedTrackingNumbers.size === 0}
                    >
                        {t('pages.delivery.temporary.batchScan.next')}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-screen bg-white">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {t('pages.delivery.temporary.batchScan.title')}
                </NavBar>
            </div>
            <div className="flex-1 min-h-0 relative">
                {isOnline ? renderOnlineMode() : renderOfflineMode()}
            </div>
        </div>
    );
};

export default ScanVerificationTemporaryPage;